import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Link,
  useColorModeValue,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import UserService from '../services/UserService';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'; // Icons for password toggle
import WebLayout from '../layouts/WebLayout';

const ForgotPassword = () => {
  const [forgotBlock, setForgotBlock] = useState(true);
  const [otpBlock, setOTPBlock] = useState(false);
  const [passwordBlock, setPasswordBlock] = useState(false);

  const bgPage = useColorModeValue('gray.100', 'gray.800');
  const bgBox = useColorModeValue('white', 'gray.700');

  const [Mobile, setMobile] = useState('');
  const [otp, setOTP] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [uId, setUId] = useState(0);

  // Loading states for buttons
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Password visibility states
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toast = useToast(); // Initialize useToast
  const navigate = useNavigate();

  // Handle initial form submission (Send OTP)
  const handleForm = async (e) => {
    e.preventDefault();
    if (!Mobile) {
      toast({
        title: 'Error',
        description: 'Please enter your mobile number.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setIsSendingOTP(true); // Set loading state
    try {
      const res = await UserService.sendOTP({ MobileNo: Mobile });
      if (res.statusCode === 200) {
        toast({
          title: 'OTP Sent',
          description: res.message || 'OTP sent to your mobile number.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setForgotBlock(false);
        setOTPBlock(true);
        setPasswordBlock(false);
      } else {
        toast({
          title: 'Error',
          description: res.message || 'Failed to send OTP. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      console.error(error);
    } finally {
      setIsSendingOTP(false); // Reset loading state
    }
  };

  // Handle OTP verification
  const handleOTP = async (e) => {
    e.preventDefault();
    if (!otp) {
      toast({
        title: 'Error',
        description: 'Please enter the OTP.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setIsVerifyingOTP(true); // Set loading state
    let params = {
      MobileNo: Mobile,
      otp: otp,
    };

    try {
      const res = await UserService.verifyOTP(params);
      if (res.statusCode === 200) {
        toast({
          title: 'OTP Verified',
          description: 'You can now set your new password.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setUId(res.data.uId);
        setForgotBlock(false);
        setOTPBlock(false);
        setPasswordBlock(true);
      } else {
        toast({
          title: 'Error',
          description: res.message || 'OTP verification failed. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      console.error(error);
    } finally {
      setIsVerifyingOTP(false); // Reset loading state
    }
  };

  // Handle resend OTP
  const resendOTP = async () => {
    if (!Mobile) {
      toast({
        title: 'Error',
        description: 'Mobile number is missing to resend OTP.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setIsSendingOTP(true); // Re-use sending OTP loading state
    let params = {
      MobileNo: Mobile,
    };

    try {
      const res = await UserService.sendOTP(params);
      if (res.statusCode === 200) {
        toast({
          title: 'OTP Resent',
          description: res.message || 'OTP has been resent to your mobile number.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Error',
          description: res.message || 'Failed to resend OTP. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      console.error(error);
    } finally {
      setIsSendingOTP(false); // Reset loading state
    }
  };

  // Handle password update
  const handlePasswordForm = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast({
        title: 'Error',
        description: 'Please fill in both password fields.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (password.length < 6) { // Basic password length validation
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setIsUpdatingPassword(true); // Set loading state
    let params = { uId: uId, password: password, confirmPassword: confirmPassword };
    try {
      const res = await UserService.forgotPasswordUpdate(params);
      if (res.statusCode === 200) {
        toast({
          title: 'Password Updated',
          description: 'Your password has been successfully changed.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setForgotBlock(false);
        setOTPBlock(false);
        setPasswordBlock(false);
        navigate('/login'); // Navigate to login after successful update
      } else {
        toast({
          title: 'Error',
          description: res.message || 'Failed to update password. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      console.error(error);
    } finally {
      setIsUpdatingPassword(false); // Reset loading state
    }
  };

  return (
    <WebLayout>
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={bgPage}
        p={4} // Add padding for small screens
      >
        {forgotBlock && (
          <Box
            rounded="md"
            bg={bgBox}
            boxShadow="lg"
            p={8}
            maxW={{ base: 'full', sm: 'md' }} // Full width on base, md on sm and up
            w="full"
          >
            <Stack spacing={4}>
              <Heading fontSize="2xl" textAlign="center">
                Forgot your password?
              </Heading>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Enter your phone number and we’ll send you an OTP.
              </Text>
              <form onSubmit={handleForm}>
                <FormControl id="mobile" isRequired> {/* Changed ID to mobile for clarity */}
                  <FormLabel fontSize="sm">Mobile Number</FormLabel> {/* Changed label */}
                  <Input
                    type="tel" // Use type="tel" for mobile keyboard
                    inputMode="numeric" // Hint for numeric keyboard
                    value={Mobile}
                    placeholder="Enter your mobile number"
                    size="sm"
                    onChange={(e) => {
                      // Only allow digits and limit length if desired
                      const value = e.target.value;
                      if (/^\d*$/.test(value) && value.length <= 10) {
                        setMobile(value);
                      }
                    }}
                  />
                </FormControl>

                <Stack spacing={4} pt={6}>
                  <Button
                    type='submit'
                    size="sm"
                    bg="green.400"
                    color="white"
                    _hover={{
                      bg: 'green.500',
                    }}
                    isLoading={isSendingOTP} // Show loading state
                    loadingText="Sending..."
                  >
                    Send OTP
                  </Button>
                </Stack>
              </form>
              <Box mt={4} textAlign="center">
                <Text fontSize="sm">
                  Remember your password?{' '} {/* Changed text for clarity */}
                  <Link as={RouterLink} to="/login" color="teal.500" fontWeight="semibold"> {/* Styled link */}
                    Login
                  </Link>
                </Text>
              </Box>
            </Stack>
          </Box>
        )}

        {otpBlock && (
          <Box
            rounded="md"
            bg={bgBox}
            boxShadow="lg"
            p={8}
            maxW={{ base: 'full', sm: 'md' }}
            w="full"
          >
            <Stack spacing={4}>
              <Heading fontSize="2xl" textAlign="center">
                Verify OTP
              </Heading>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                An OTP has been sent to your mobile number.
              </Text>
              <form onSubmit={handleOTP}>
                <FormControl id="otp" isRequired> {/* Changed ID to otp */}
                  <FormLabel fontSize="sm">OTP</FormLabel>
                  <Input
                    type="tel" // Use type="tel" for numeric keyboard
                    inputMode="numeric" // Hint for numeric keyboard
                    value={otp}
                    placeholder="Enter OTP"
                    size="sm"
                    maxLength={6} // Common OTP length
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value) && value.length <= 6) {
                        setOTP(value);
                      }
                    }}
                  />
                </FormControl>

                <Stack spacing={4} pt={4} direction='row' align='center' justify="center"> {/* Added justify="center" */}
                  <Button
                    type='submit'
                    size="sm"
                    bg="green.400"
                    color="white"
                    _hover={{
                      bg: 'green.500',
                    }}
                    isLoading={isVerifyingOTP} // Show loading state
                    loadingText="Verifying..."
                  >
                    Verify OTP
                  </Button>

                  <Button
                    size='sm'
                    bg="red.400"
                    color="white"
                    type="button"
                    _hover={{
                      bg: 'red.500',
                    }}
                    onClick={resendOTP}
                    isLoading={isSendingOTP} // Re-use sending OTP loading state
                    loadingText="Resending..."
                  >
                    Resend OTP
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Box>
        )}

        {passwordBlock && (
          <Box
            rounded="md"
            bg={bgBox}
            boxShadow="lg"
            p={8}
            maxW={{ base: 'full', sm: 'md' }}
            w="full"
          >
            <Stack spacing={4}>
              <Heading fontSize="md" textAlign="center">
                Set New Password
              </Heading>
              <form onSubmit={handlePasswordForm}>
                <FormControl id="newPassword" isRequired> {/* Changed ID */}
                  <FormLabel fontSize="sm">New Password</FormLabel>
                  <InputGroup size='sm'>
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={password}
                      placeholder="New Password"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <InputRightElement width='2.5rem'>
                      <IconButton
                        h='1.75rem'
                        size='sm'
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        icon={showNewPassword ? <ViewOffIcon /> : <ViewIcon />}
                        aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl id="confirmPassword" isRequired mt={4}> {/* Changed ID, added mt */}
                  <FormLabel fontSize="sm">Confirm Password</FormLabel>
                  <InputGroup size='sm'>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      placeholder="Confirm Password"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <InputRightElement width='2.5rem'>
                      <IconButton
                        h='1.75rem'
                        size='sm'
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Stack spacing={4} pt={6}>
                  <Button
                    type="submit"
                    size="sm"
                    bg="green.400"
                    color="white"
                    _hover={{
                      bg: 'green.500',
                    }}
                    isLoading={isUpdatingPassword} // Show loading state
                    loadingText="Updating..."
                  >
                    Update Password
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Box>
        )}
      </Flex>
    </WebLayout>
  );
};

export default ForgotPassword;